import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import { MapService } from 'src/app/services/map.service';
import 'leaflet-routing-machine';
import { Subject, takeUntil } from 'rxjs';
@Component({
  selector: 'app-leaflet-map',
  templateUrl: './leaflet-map.component.html',
  styleUrls: ['./leaflet-map.component.scss']
})
export class LeafletMapComponent implements OnDestroy, AfterViewInit {
  public markers: IMarker[] = [];
  private map: L.Map;
  private subscriptionsUnsubscribe = new Subject<void>();
  private routing: any;
  constructor(
    private _mapService: MapService,
    private _cdRef: ChangeDetectorRef,) { }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.subscriptionsUnsubscribe.next();
  }

  private initMap(): void {
    this.map = L.map('map', {
      center: [39.8285013, -98.5794589],
      zoom: 3
    });
    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });
    tiles.addTo(this.map);
    this.subscriptions();


  }
  private subscriptions() {
    this._mapService.markerUpdated$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((locationObj) => {
      switch (locationObj?.type) {
        case 'ORIGIN':
          this.loadOrigin(locationObj?.address);
          break;
        case 'DESTINATION':
          this.loadDestination(locationObj?.address);
          break;
        case 'ROUTING':
          this.loadRouting();
          break;
        case 'REMOVE_ALL':
          this.removeAllMarkers();
          break;
      }

      this._cdRef.detectChanges();
    });
  }
  private loadOrigin(originAddress) {
    if (originAddress) {
      let origin = null;
      if (!originAddress?.draggable && originAddress?.address1?.length > 0) {
        origin = {
          address: originAddress.address1,
          title: originAddress.name,
          lat: originAddress.lat,
          lng: originAddress.lon,
          fullAddress: originAddress.address1 + ', ' + originAddress.city + ', ' + originAddress.state + ', ' + originAddress.zip + ', ' + (originAddress.country === 'US' ? 'USA' : 'Canada'),
          validationCountry: originAddress.country,
          fullAddressType: 'complete'
        };
      } else if (originAddress?.draggable) {
        origin = {
          title: originAddress.name,
          lat: originAddress.lat,
          lng: originAddress.lon,
          fullAddress: originAddress.zip ? originAddress.zip : originAddress.city,
          validationCountry: originAddress.country,
          fullAddressType: originAddress.zip ? 'zip' : 'city',
          draggable: true
        };
      }
      this.removeMarkerByType(MarkerType.ORIGIN);
      this.geocodeAddress(origin, 'ORIGIN');
    }

  }
  private loadDestination(destAddress) {
    if (destAddress) {
      let destination = {
        address: destAddress?.address1,
        title: destAddress.name,
        lat: destAddress.lat,
        lng: destAddress.lon,
        fullAddress: destAddress.address1 + ', ' + destAddress.city + ', ' + destAddress.state + ', ' + destAddress.zip + ', ' + (destAddress.country === 'US' ? 'USA' : 'Canada'),
        validationCountry: destAddress.country,
        fullAddressType: 'complete'
      };
      this.removeMarkerByType(MarkerType.DESTINATION);
      this.geocodeAddress(destination, 'DESTINATION');
    }

  }
  private loadRouting() {
    let waypoints = this.markers.map(x => L.latLng(x.position.lat, x.position.lng));
    if (this.routing) {
      this.map.removeControl(this.routing);
    }
    this.routing = L.Routing.control({
      waypoints: waypoints,
      router: L.Routing.osrmv1({
        serviceUrl: `https://router.project-osrm.org/route/v1`
      }),
      plan: L.Routing.plan(waypoints, {
        createMarker: function () { return null; },
        addWaypoints: false,
      }),
      addWaypoints: false,
      routeWhileDragging: false,
      show: false
    }).addTo(this.map);


  }
  private geocodeAddress(point, type) {
    const marker = {
      position: { lat: point.lat, lng: point.lng },
      title: type,
      type: type
    };
    this.addMarker(marker);
    this.fitBounds();
  }
  private addMarker(marker: IMarker) {
    const index = this.markers.findIndex((m) => m.title === marker.title);
    if (index === -1) {
      let icon = {
        icon: L.icon({
          iconSize: [25, 41],
          iconAnchor: [13, 41],
          // specify the path here
          iconUrl: './assets/images/leaflet/marker-icon.png',
          shadowUrl: './assets/images/leaflet/marker-shadow.png'
        })
      };
      let leafletMarker = L.marker([marker.position.lat, marker.position.lng], icon)
        .addTo(this.map)
        .bindPopup(marker.title);

      marker.leafletMarker = leafletMarker;
      this.markers.push(marker);
      leafletMarker.on('mouseover', function (e) {
        this.openPopup();
      });
      leafletMarker.on('mouseout', function (e) {
        this.closePopup();
      });
    }
  }
  private fitBounds() {
    if(this.markers?.length){
      var featureGroup = L.featureGroup(this.markers.map(x => x.leafletMarker)).addTo(this.map);
      this.map.fitBounds(featureGroup.getBounds());
    }

  };
  private removeMarkerByType(type: MarkerType): void {
    for (let i = this.markers.length - 1; i >= 0; i--) {
      if (this.markers[i].type === type) {
        this.removeMarkerOnLayer(this.markers[i]);
        this.markers.splice(i, 1);

      }
    }
    this.fitBounds();
  }
  private removeAllMarkers(): void {
    for (let i = this.markers.length - 1; i >= 0; i--) {
      this.removeMarkerOnLayer(this.markers[i]);
      this.markers.splice(i, 1);
    }
    this.fitBounds();
  }
  private removeMarkerOnLayer(marker: IMarker) {
    this.map.removeLayer(marker.leafletMarker);
  }

}

interface IMarker {
  position: { lat: number, lng: number }
  title: string;
  leafletMarker?: L.Marker,
  type: MarkerType
}

export enum MarkerType {
  ORIGIN = 'ORIGIN',
  DESTINATION = 'DESTINATION'
}
